import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import {
  MOVE_IDS,
  type MoveId,
  inferMove,
  isYogaTitle,
  looksLikeExercise,
} from '@goaltree/backend/convex/lib/exerciseMoves'
import {
  type ItemKind,
  MAX_CUE,
  MAX_ITEMS,
  MAX_NAME,
  MAX_SESSIONS,
  type PlanItem,
  type PlanProfile,
  type PlanSession,
  type Progression,
  newId,
  restSeconds,
} from '@goaltree/backend/convex/lib/sessionPlan'
import { cn } from '@goaltree/ui/lib/utils'
import { useAction, useMutation, useQuery } from 'convex/react'
import type { TFunction } from 'i18next'
import {
  Check,
  ChevronDown,
  ChevronUp,
  Maximize2,
  Pencil,
  Play,
  Plus,
  Sparkles,
  Trash2,
  TrendingUp,
  X,
} from 'lucide-react'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { useLiveHabit } from '@/components/habit-stack'
import { clock } from '@/components/number-habit'
import { SessionFullscreen } from '@/components/session-fullscreen'
import {
  KindIcon,
  type PlanView,
  type Runner,
  SetRows,
  duration,
  num,
  targetLine,
  useSessionRunner,
} from '@/components/session-runner'
import { haptic } from '@/lib/haptics'
import { useSessionPlanUi } from '@/lib/session-plan-store'
import { taskTitle } from '@/lib/tags'
import { handleUsageLimit } from '@/lib/usage-limit'

// Workout session plans on a habit: build or ask the coach for one, then run
// today's session fullscreen (or as the sheet's checklist). Finishing ticks the
// habit via `onComplete` (the caller's normal completion path).

function progressionAmount(t: TFunction, p: Progression, n: number, lang: string) {
  if (p.field === 'seconds') return duration(t, n, lang)
  if (p.field === 'reps') return t('sessionPlan.repsN', { count: n })
  return `${num(n, lang)} ${p.unit ?? ''}`.trim()
}

export function planMetaText(t: TFunction, s: { name: string; items: number }) {
  const count = t('sessionPlan.exercises', { count: s.items })
  return s.name ? `${s.name} · ${count}` : count
}

const rowPill =
  'inline-flex min-w-0 cursor-pointer items-center gap-1.5 rounded-full bg-primary/10 py-1 pr-2.5 pl-2 text-xs font-semibold text-primary-text transition-colors hover:bg-primary/20'

// An unplanned habit that looks like exercise gets a "Build a plan" chip under its
// title, opening the sheet on the plan step.
export function HabitPlanLine({ id, className }: { id: Id<'nodes'>; className?: string }) {
  const { t } = useTranslation()
  const habit = useLiveHabit(id)
  const dismiss = useMutation(api.sessionPlans.dismissPlanChip).withOptimisticUpdate((store) => {
    const habits = store.getQuery(api.nodes.getHabits, {})
    if (habits)
      store.setQuery(
        api.nodes.getHabits,
        {},
        habits.map((h) => (h._id === id ? { ...h, planChipDismissed: true } : h)),
      )
  })
  const ui = useSessionPlanUi()
  if (!habit) return null
  const s = habit.sessionPlan
  if (!s) {
    if (habit.planChipDismissed || !looksLikeExercise(taskTitle(habit), habit.category)) return null
    return (
      <div className={cn('flex min-w-0 items-center gap-0.5', className)} data-testid="plan-chip">
        <button type="button" onClick={() => ui.requestChoose(id)} className={rowPill}>
          <Plus className="size-3 shrink-0" strokeWidth={2.6} />
          <span className="truncate">{t('sessionPlan.buildPlan')}</span>
        </button>
        <button
          type="button"
          aria-label={t('sessionPlan.buildPlanDismiss')}
          title={t('sessionPlan.buildPlanDismiss')}
          onClick={(e) => {
            e.stopPropagation()
            void dismiss({ habitId: id }).catch(() => toast.error(t('sessionPlan.error')))
          }}
          className="flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-full text-muted-foreground/70 transition-colors hover:bg-muted hover:text-foreground"
        >
          <X className="size-3" strokeWidth={2.4} />
        </button>
      </div>
    )
  }
  // A planned habit's start and progress live in HabitSessionRow, like a number habit's.
  return null
}

const sectionLabel = 'text-[10px] font-semibold uppercase tracking-widest text-muted-foreground'
const btn =
  'inline-flex cursor-pointer items-center justify-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50'
const primaryBtn = cn(btn, 'bg-primary text-primary-foreground hover:bg-primary/90')
const ghostBtn = cn(btn, 'text-muted-foreground hover:bg-muted hover:text-foreground')
const outlineBtn = cn(btn, 'border border-border/70 text-foreground hover:bg-muted')
const input =
  'h-8 min-w-0 rounded-md border border-border/70 bg-background px-2 text-sm text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring/50'

type Mode = 'view' | 'choose' | 'ask' | 'edit'

export function SessionPlanSection({
  habitId,
  doneToday,
  onComplete,
  coachName,
}: {
  habitId: Id<'nodes'>
  doneToday: boolean
  onComplete: (id: Id<'nodes'>) => void
  coachName: string
}) {
  const { t } = useTranslation()
  const data = useQuery(api.sessionPlans.getPlan, { habitId })
  const habit = useLiveHabit(habitId)
  const [mode, setMode] = useState<Mode>('view')
  const [draft, setDraft] = useState<PlanSession[]>([])
  const [profile, setProfile] = useState<PlanProfile | undefined>()
  const ref = useRef<HTMLDivElement>(null)
  const chooseFor = useSessionPlanUi((s) => s.chooseFor)
  const clearChoose = useSessionPlanUi((s) => s.clearChoose)
  const fullscreen = useSessionPlanUi((s) => s.fullscreen === habitId)
  const setFullscreen = useSessionPlanUi((s) => s.setFullscreen)
  const runner = useSessionRunner({
    habitId,
    view: data ?? undefined,
    session: data?.session,
    doneToday,
    onComplete,
    quiet: fullscreen,
  })
  const hasPlan = !!data?.plan
  const yoga = isYogaTitle(habit ? taskTitle(habit) : '')

  useEffect(() => {
    setMode('view')
    return () => {
      if (useSessionPlanUi.getState().fullscreen === habitId) setFullscreen(null)
    }
  }, [habitId, setFullscreen])

  useEffect(() => {
    if (chooseFor !== habitId || !data) return
    clearChoose()
    if (data.plan) return
    setMode('choose')
    requestAnimationFrame(() =>
      ref.current?.scrollIntoView({ block: 'center', behavior: 'smooth' }),
    )
  }, [chooseFor, habitId, data, clearChoose])

  const { reset } = runner
  useEffect(() => {
    if (!fullscreen) reset()
  }, [fullscreen, reset])

  if (!data) return null

  const openEditor = (sessions: PlanSession[], p?: PlanProfile) => {
    setDraft(sessions)
    setProfile(p)
    setMode('edit')
  }
  const showFull = fullscreen && (!!runner.session || !!runner.finished)

  return (
    <div ref={ref} className="flex min-w-0 flex-col gap-2" data-testid="session-plan">
      <span className={sectionLabel}>{t('sessionPlan.section')}</span>
      {mode === 'edit' ? (
        <PlanEditor
          habitId={habitId}
          initial={draft}
          profile={profile}
          canRemove={hasPlan}
          onDone={() => setMode('view')}
        />
      ) : mode === 'ask' ? (
        <AskCoachForm
          habitId={habitId}
          coachName={coachName}
          yoga={yoga}
          initial={data.plan?.profile ?? undefined}
          onCancel={() => setMode(hasPlan ? 'view' : 'choose')}
          onPlanned={openEditor}
        />
      ) : !data.plan ? (
        mode === 'choose' ? (
          <div className="flex flex-wrap gap-2" data-testid="plan-choose">
            <button type="button" className={primaryBtn} onClick={() => setMode('ask')}>
              <Sparkles className="size-3.5" />
              {t('sessionPlan.askCoach', { coach: coachName })}
            </button>
            <button
              type="button"
              className={outlineBtn}
              onClick={() => openEditor([blankSession(t)])}
            >
              <Pencil className="size-3.5" />
              {t('sessionPlan.buildMyself')}
            </button>
            <button type="button" className={ghostBtn} onClick={() => setMode('view')}>
              {t('sessionPlan.cancel')}
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setMode('choose')}
            className="flex w-fit cursor-pointer items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground"
          >
            <Plus className="size-3.5" />
            {t('sessionPlan.add')}
          </button>
        )
      ) : runner.session ? (
        <SessionRunner
          habitId={habitId}
          view={data}
          runner={runner}
          onFullscreen={() => setFullscreen(habitId)}
        />
      ) : (
        <PlanOverview
          habitId={habitId}
          view={data}
          onStart={() => setFullscreen(habitId)}
          onEdit={() => openEditor(data.plan!.sessions, data.plan!.profile ?? undefined)}
          onReplan={() => setMode('ask')}
          coachName={coachName}
        />
      )}
      {(data.plan || mode === 'edit') && (
        <p className="text-[11px] leading-snug text-muted-foreground/80">
          {t('sessionPlan.safety')}
        </p>
      )}
      {showFull && (
        <SessionFullscreen
          runner={runner}
          habitId={habitId}
          title={habit ? taskTitle(habit) : ''}
          onMinimize={() => setFullscreen(null)}
        />
      )}
    </div>
  )
}

const blankItem = (): PlanItem => ({ id: newId(), name: '', kind: 'sets', sets: 3, reps: 10 })
const blankSession = (t: TFunction, n = 0): PlanSession => ({
  id: newId(),
  name: n ? t('sessionPlan.sessionN', { n: n + 1 }) : '',
  items: [blankItem()],
})

function PlanOverview({
  habitId,
  view,
  onStart,
  onEdit,
  onReplan,
  coachName,
}: {
  habitId: Id<'nodes'>
  view: PlanView
  onStart: () => void
  onEdit: () => void
  onReplan: () => void
  coachName: string
}) {
  const { t, i18n } = useTranslation()
  const plan = view.plan!
  const start = useMutation(api.sessionPlans.startSession)
  const [shown, setShown] = useState(plan.next)
  const doneToday = view.session?.status === 'done'
  const s = plan.sessions[Math.min(shown, plan.sessions.length - 1)]
  const begin = (sessionId?: string) => {
    onStart()
    void start({ habitId, sessionId }).catch(() => toast.error(t('sessionPlan.error')))
  }

  return (
    <div className="flex flex-col gap-2">
      {plan.sessions.length > 1 && (
        <div className="flex flex-wrap gap-1" role="tablist">
          {plan.sessions.map((x, i) => (
            <button
              key={x.id}
              type="button"
              role="tab"
              aria-selected={i === shown}
              onClick={() => setShown(i)}
              className={cn(
                'cursor-pointer rounded-full px-2.5 py-1 text-xs font-semibold transition-colors',
                i === shown
                  ? 'bg-foreground text-background'
                  : 'bg-muted/60 text-muted-foreground hover:text-foreground',
              )}
            >
              {x.name || t('sessionPlan.sessionN', { n: i + 1 })}
              {i === plan.next && !doneToday && (
                <span className="ml-1 font-normal opacity-70">· {t('sessionPlan.next')}</span>
              )}
            </button>
          ))}
        </div>
      )}
      <ul className="flex flex-col divide-y divide-border/50 rounded-xl border border-border/60">
        {s.items.map((i) => (
          <li key={i.id} className="flex items-start gap-2.5 px-3 py-2">
            <KindIcon kind={i.kind} className="mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="flex flex-wrap items-baseline gap-x-2 text-sm font-medium text-foreground">
                {i.name}
                <span className="font-mono text-xs font-normal text-muted-foreground">
                  {targetLine(t, i, i18n.language)}
                  {i.eachSide && ` · ${t('sessionPlan.eachSide')}`}
                </span>
              </p>
              {i.cue && <p className="text-xs text-muted-foreground">{i.cue}</p>}
            </div>
          </li>
        ))}
      </ul>
      {view.balance && !doneToday && (
        <BalanceChip habitId={habitId} balance={view.balance} coachName={coachName} />
      )}
      {view.last && (
        <p className="text-xs text-muted-foreground">
          {t('sessionPlan.lastSession', {
            name: view.last.sessionName || t('sessionPlan.section'),
            done: view.last.done,
            total: view.last.total,
          })}
          {view.last.prs.length > 0 &&
            ` · ${t('sessionPlan.prs', { list: view.last.prs.join(', ') })}`}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-1.5">
        {doneToday ? (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary-text">
            <Check className="size-3.5" />
            {t('sessionPlan.doneToday', { name: view.session?.sessionName ?? '' })}
          </span>
        ) : (
          <button
            type="button"
            className={primaryBtn}
            onClick={() => begin(shown === plan.next ? undefined : s.id)}
          >
            <Play className="size-3.5" />
            {plan.sessions.length > 1 && s.name
              ? t('sessionPlan.startNamed', { name: s.name })
              : t('sessionPlan.start')}
          </button>
        )}
        <button type="button" className={ghostBtn} onClick={onEdit}>
          <Pencil className="size-3.5" />
          {t('sessionPlan.edit')}
        </button>
        <button type="button" className={ghostBtn} onClick={onReplan}>
          <Sparkles className="size-3.5" />
          {t('sessionPlan.replan', { coach: coachName })}
        </button>
      </div>
    </div>
  )
}

export function ProgressionChips({
  habitId,
  items,
}: {
  habitId: Id<'nodes'>
  items: Progression[]
}) {
  const { t, i18n } = useTranslation()
  const accept = useMutation(api.sessionPlans.acceptProgression)
  const dismiss = useMutation(api.sessionPlans.dismissProgression)
  if (!items.length) return null
  const args = (p: Progression) => ({
    habitId,
    itemId: p.itemId,
    field: p.field,
    from: p.from,
    to: p.to,
  })
  return (
    <div className="flex flex-col gap-1.5">
      {items.map((p) => (
        <div
          key={`${p.itemId}:${p.field}`}
          className="rounded-xl border border-primary/30 bg-primary/5 px-3 py-2"
        >
          <p className="flex gap-2 text-xs leading-snug text-foreground">
            <TrendingUp className="mt-px size-3.5 shrink-0 text-primary-text" strokeWidth={2.5} />
            {t('sessionPlan.progressLine', {
              name: p.name,
              from: progressionAmount(t, p, p.from, i18n.language),
              to: progressionAmount(t, p, p.to, i18n.language),
            })}
          </p>
          <div className="mt-1.5 flex gap-1">
            <button
              type="button"
              className={cn(primaryBtn, 'py-1')}
              onClick={() => {
                haptic('success')
                void accept(args(p)).catch(() => toast.error(t('sessionPlan.error')))
              }}
            >
              {t('sessionPlan.accept', { amount: progressionAmount(t, p, p.to, i18n.language) })}
            </button>
            <button
              type="button"
              className={cn(ghostBtn, 'py-1')}
              onClick={() => void dismiss(args(p)).catch(() => toast.error(t('sessionPlan.error')))}
            >
              {t('sessionPlan.notNow')}
            </button>
          </div>
        </div>
      ))}
    </div>
  )
}

function BalanceChip({
  habitId,
  balance: b,
  coachName,
}: {
  habitId: Id<'nodes'>
  balance: NonNullable<PlanView['balance']>
  coachName: string
}) {
  const { t, i18n } = useTranslation()
  const first = useMutation(api.sessionPlans.moveSessionFirst)
  const dismiss = useMutation(api.sessionPlans.dismissBalance)
  const shorten = useAction(api.sessionPlansNode.shorten)
  const [busy, setBusy] = useState(false)
  const fail = () => toast.error(t('sessionPlan.error'))
  const args = { habitId, sessionId: b.sessionId }
  return (
    <div
      className="rounded-xl border border-primary/30 bg-primary/5 px-3 py-2"
      data-testid="balance-chip"
    >
      <p className="flex gap-2 text-xs leading-snug text-foreground">
        <TrendingUp className="mt-px size-3.5 shrink-0 text-primary-text" strokeWidth={2.5} />
        {b.kind === 'slipping'
          ? t('sessionPlan.balanceSlipping', { name: b.name })
          : t('sessionPlan.balanceDominant', { name: b.name, over: b.over ?? '' })}
      </p>
      <div className="mt-1.5 flex flex-wrap gap-1">
        <button
          type="button"
          className={cn(primaryBtn, 'py-1')}
          disabled={busy}
          onClick={() =>
            void first(args)
              .then(() => toast.success(t('sessionPlan.movedFirst', { name: b.name })))
              .catch(fail)
          }
        >
          {t('sessionPlan.moveFirst')}
        </button>
        <button
          type="button"
          className={cn(outlineBtn, 'py-1')}
          disabled={busy}
          onClick={() => {
            setBusy(true)
            shorten({ ...args, locale: i18n.language, tzOffsetMin: new Date().getTimezoneOffset() })
              .then(() => toast.success(t('sessionPlan.shortened')))
              .catch((e) => {
                if (!handleUsageLimit(e)) fail()
              })
              .finally(() => setBusy(false))
          }}
        >
          <Sparkles className={cn('size-3', busy && 'animate-pulse')} />
          {busy ? t('sessionPlan.shortening', { coach: coachName }) : t('sessionPlan.makeShorter')}
        </button>
        <button
          type="button"
          className={cn(ghostBtn, 'py-1')}
          disabled={busy}
          onClick={() => void dismiss(args).catch(fail)}
        >
          {t('sessionPlan.notNow')}
        </button>
      </div>
    </div>
  )
}

function SessionRunner({
  habitId,
  view,
  runner,
  onFullscreen,
}: {
  habitId: Id<'nodes'>
  view: PlanView
  runner: Runner
  onFullscreen: () => void
}) {
  const { t } = useTranslation()
  const plan = view.plan!
  const session = runner.session!
  const start = useMutation(api.sessionPlans.startSession)

  return (
    <div className="flex flex-col gap-2.5" data-testid="session-runner">
      <div className="flex flex-wrap items-center gap-1.5">
        {plan.sessions.length > 1 && runner.untouched ? (
          plan.sessions.map((x) => (
            <button
              key={x.id}
              type="button"
              onClick={() => void start({ habitId, sessionId: x.id })}
              className={cn(
                'cursor-pointer rounded-full px-2.5 py-1 text-xs font-semibold transition-colors',
                x.id === session.sessionId
                  ? 'bg-foreground text-background'
                  : 'bg-muted/60 text-muted-foreground hover:text-foreground',
              )}
            >
              {x.name}
            </button>
          ))
        ) : (
          <span className="text-sm font-semibold text-foreground">
            {session.sessionName || t('sessionPlan.today')}
          </span>
        )}
        <button type="button" className={cn(outlineBtn, 'ml-auto py-1')} onClick={onFullscreen}>
          <Maximize2 className="size-3" />
          {t('sessionPlan.fullscreen')}
        </button>
      </div>
      <ProgressionChips habitId={habitId} items={view.progressions} />
      <ul className="flex flex-col gap-2">
        {session.items.map((l) => {
          const cue = runner.planItem(l.itemId)?.cue
          const done = l.sets.every((s) => s.done)
          return (
            <li
              key={l.itemId}
              className={cn(
                'rounded-xl border border-border/60 px-3 py-2 transition-colors',
                done && 'border-primary/30 bg-primary/5',
              )}
            >
              <div className="flex items-start gap-2.5">
                <KindIcon kind={l.kind} className="mt-0.5" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-foreground">{l.name}</p>
                  {cue && <p className="text-xs text-muted-foreground">{cue}</p>}
                </div>
              </div>
              <div className="mt-1.5 pl-[26px]">
                <SetRows runner={runner} log={l} />
              </div>
            </li>
          )
        })}
      </ul>
      {runner.rest && (
        <p className="flex items-center gap-2 font-mono text-xs tabular-nums text-muted-foreground">
          {t('sessionPlan.restFor', { time: clock(Math.max(0, runner.rest.endsAt - runner.now)) })}
          <button
            type="button"
            className={cn(ghostBtn, 'px-2 py-0.5')}
            onClick={() => runner.addRest()}
          >
            {t('sessionPlan.restMore')}
          </button>
          <button type="button" className={cn(ghostBtn, 'px-2 py-0.5')} onClick={runner.skipRest}>
            {t('sessionPlan.restSkip')}
          </button>
        </p>
      )}
      <div className="flex items-center gap-2">
        <button
          type="button"
          className={runner.remaining ? outlineBtn : primaryBtn}
          onClick={runner.finish}
        >
          <Check className="size-3.5" />
          {t('sessionPlan.finish')}
        </button>
        {runner.remaining > 0 && !runner.untouched && (
          <span className="text-[11px] text-muted-foreground">
            {t('sessionPlan.skippedNote', { count: runner.remaining })}
          </span>
        )}
      </div>
    </div>
  )
}

function Chips<T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T | undefined
  options: { value: T; label: string }[]
  onChange: (v: T) => void
  label: string
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <div className="flex flex-wrap gap-1" role="radiogroup" aria-label={label}>
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={value === o.value}
            onClick={() => onChange(o.value)}
            className={cn(
              'cursor-pointer rounded-full px-2.5 py-1 text-xs font-semibold transition-colors',
              value === o.value
                ? 'bg-foreground text-background'
                : 'bg-muted/60 text-muted-foreground hover:text-foreground',
            )}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  )
}

function AskCoachForm({
  habitId,
  coachName,
  yoga,
  initial,
  onCancel,
  onPlanned,
}: {
  habitId: Id<'nodes'>
  coachName: string
  // Yoga / stretching: experience and a focus instead of equipment.
  yoga: boolean
  initial?: PlanProfile
  onCancel: () => void
  onPlanned: (sessions: PlanSession[], profile: PlanProfile) => void
}) {
  const { t, i18n } = useTranslation()
  const generate = useAction(api.sessionPlansNode.generate)
  const [level, setLevel] = useState(initial?.level ?? 'beginner')
  const [equipment, setEquipment] = useState(initial?.equipment ?? 'none')
  const [focus, setFocus] = useState(initial?.focus ?? 'flexibility')
  const [minutes, setMinutes] = useState(String(initial?.minutes ?? 30))
  const [busy, setBusy] = useState(false)

  const submit = () => {
    const m = Math.round(Number(minutes))
    const profile: PlanProfile = {
      level,
      ...(yoga ? { focus } : { equipment }),
      ...(Number.isFinite(m) && m >= 5 && m <= 180 && { minutes: m }),
    }
    setBusy(true)
    generate({
      habitId,
      profile,
      locale: i18n.language,
      tzOffsetMin: new Date().getTimezoneOffset(),
    })
      .then((sessions) => onPlanned(sessions, profile))
      .catch((e) => {
        if (!handleUsageLimit(e)) toast.error(t('sessionPlan.planError'))
      })
      .finally(() => setBusy(false))
  }

  return (
    <div className="flex flex-col gap-2.5 rounded-xl border border-border/60 p-3">
      <p className="text-xs text-muted-foreground">
        {t('sessionPlan.askIntro', { coach: coachName })}
      </p>
      <Chips
        label={t(yoga ? 'sessionPlan.experience' : 'sessionPlan.level')}
        value={level}
        onChange={setLevel}
        options={[
          { value: 'beginner', label: t('sessionPlan.levelBeginner') },
          { value: 'intermediate', label: t('sessionPlan.levelIntermediate') },
          { value: 'advanced', label: t('sessionPlan.levelAdvanced') },
        ]}
      />
      {yoga ? (
        <Chips
          label={t('sessionPlan.focus')}
          value={focus}
          onChange={setFocus}
          options={[
            { value: 'flexibility', label: t('sessionPlan.focusFlexibility') },
            { value: 'strength', label: t('sessionPlan.focusStrength') },
            { value: 'relax', label: t('sessionPlan.focusRelax') },
          ]}
        />
      ) : (
        <Chips
          label={t('sessionPlan.equipment')}
          value={equipment}
          onChange={setEquipment}
          options={[
            { value: 'none', label: t('sessionPlan.equipNone') },
            { value: 'dumbbells', label: t('sessionPlan.equipDumbbells') },
            { value: 'gym', label: t('sessionPlan.equipGym') },
          ]}
        />
      )}
      <label className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
        {t('sessionPlan.minutes')}
        <input
          type="number"
          inputMode="numeric"
          min={5}
          max={180}
          value={minutes}
          onChange={(e) => setMinutes(e.target.value)}
          className={cn(input, 'w-16 text-center')}
        />
      </label>
      <div className="flex gap-1.5">
        <button type="button" className={primaryBtn} disabled={busy} onClick={submit}>
          <Sparkles className={cn('size-3.5', busy && 'animate-pulse')} />
          {busy ? t('sessionPlan.planning', { coach: coachName }) : t('sessionPlan.planIt')}
        </button>
        <button type="button" className={ghostBtn} disabled={busy} onClick={onCancel}>
          {t('sessionPlan.cancel')}
        </button>
      </div>
    </div>
  )
}

function move<T>(list: T[], from: number, to: number): T[] {
  if (to < 0 || to >= list.length) return list
  const next = [...list]
  const [x] = next.splice(from, 1)
  next.splice(to, 0, x)
  return next
}

function IconBtn({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string
  onClick: () => void
  disabled?: boolean
  children: ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className="flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-default disabled:opacity-30 disabled:hover:bg-transparent"
    >
      {children}
    </button>
  )
}

function PlanEditor({
  habitId,
  initial,
  profile,
  canRemove,
  onDone,
}: {
  habitId: Id<'nodes'>
  initial: PlanSession[]
  profile?: PlanProfile
  canRemove: boolean
  onDone: () => void
}) {
  const { t } = useTranslation()
  const save = useMutation(api.sessionPlans.savePlan)
  const remove = useMutation(api.sessionPlans.deletePlan)
  const [sessions, setSessions] = useState<PlanSession[]>(initial)
  const [confirmRemove, setConfirmRemove] = useState(false)
  const [busy, setBusy] = useState(false)

  const setSession = (si: number, patch: Partial<PlanSession>) =>
    setSessions((ss) => ss.map((s, i) => (i === si ? { ...s, ...patch } : s)))
  const setItem = (si: number, ii: number, patch: Partial<PlanItem>) =>
    setSessions((ss) =>
      ss.map((s, i) =>
        i === si ? { ...s, items: s.items.map((x, j) => (j === ii ? { ...x, ...patch } : x)) } : s,
      ),
    )
  const valid = sessions.some((s) => s.items.some((i) => i.name.trim()))

  const submit = () => {
    if (!valid) return void toast.error(t('sessionPlan.emptyEditor'))
    setBusy(true)
    save({ habitId, sessions, profile })
      .then(() => {
        toast.success(t('sessionPlan.saved'))
        onDone()
      })
      .catch(() => toast.error(t('sessionPlan.error')))
      .finally(() => setBusy(false))
  }

  return (
    <div className="flex flex-col gap-3" data-testid="plan-editor">
      {sessions.map((s, si) => (
        <div key={s.id} className="flex flex-col gap-2 rounded-xl border border-border/60 p-2.5">
          <div className="flex items-center gap-1">
            <input
              value={s.name}
              maxLength={MAX_NAME}
              placeholder={t('sessionPlan.sessionPlaceholder')}
              aria-label={t('sessionPlan.sessionName')}
              onChange={(e) => setSession(si, { name: e.target.value })}
              className={cn(input, 'flex-1 font-semibold')}
            />
            {sessions.length > 1 && (
              <>
                <IconBtn
                  label={t('sessionPlan.moveUp')}
                  disabled={si === 0}
                  onClick={() => setSessions((ss) => move(ss, si, si - 1))}
                >
                  <ChevronUp className="size-4" />
                </IconBtn>
                <IconBtn
                  label={t('sessionPlan.moveDown')}
                  disabled={si === sessions.length - 1}
                  onClick={() => setSessions((ss) => move(ss, si, si + 1))}
                >
                  <ChevronDown className="size-4" />
                </IconBtn>
                <IconBtn
                  label={t('sessionPlan.deleteSession')}
                  onClick={() => setSessions((ss) => ss.filter((_, i) => i !== si))}
                >
                  <X className="size-4" />
                </IconBtn>
              </>
            )}
          </div>
          {s.items.map((it, ii) => (
            <div key={it.id} className="flex flex-col gap-1.5 rounded-lg bg-muted/30 p-2">
              <div className="flex items-center gap-1">
                <KindIcon kind={it.kind} />
                <input
                  value={it.name}
                  maxLength={MAX_NAME}
                  placeholder={t('sessionPlan.itemPlaceholder')}
                  aria-label={t('sessionPlan.itemName')}
                  onChange={(e) => setItem(si, ii, { name: e.target.value })}
                  className={cn(input, 'flex-1')}
                />
                <IconBtn
                  label={t('sessionPlan.moveUp')}
                  disabled={ii === 0}
                  onClick={() => setSession(si, { items: move(s.items, ii, ii - 1) })}
                >
                  <ChevronUp className="size-4" />
                </IconBtn>
                <IconBtn
                  label={t('sessionPlan.moveDown')}
                  disabled={ii === s.items.length - 1}
                  onClick={() => setSession(si, { items: move(s.items, ii, ii + 1) })}
                >
                  <ChevronDown className="size-4" />
                </IconBtn>
                <IconBtn
                  label={t('sessionPlan.delete')}
                  onClick={() => setSession(si, { items: s.items.filter((_, j) => j !== ii) })}
                >
                  <Trash2 className="size-3.5" />
                </IconBtn>
              </div>
              <ItemTargetEditor item={it} onChange={(p) => setItem(si, ii, p)} />
              <input
                value={it.cue ?? ''}
                maxLength={MAX_CUE}
                placeholder={t('sessionPlan.cuePlaceholder')}
                aria-label={t('sessionPlan.cue')}
                onChange={(e) => setItem(si, ii, { cue: e.target.value })}
                className={cn(input, 'text-xs')}
              />
            </div>
          ))}
          {s.items.length < MAX_ITEMS && (
            <button
              type="button"
              className={cn(ghostBtn, 'w-fit')}
              onClick={() => setSession(si, { items: [...s.items, blankItem()] })}
            >
              <Plus className="size-3.5" />
              {t('sessionPlan.addItem')}
            </button>
          )}
        </div>
      ))}
      {sessions.length < MAX_SESSIONS && (
        <button
          type="button"
          className={cn(ghostBtn, 'w-fit')}
          onClick={() => setSessions((ss) => [...ss, blankSession(t, ss.length)])}
        >
          <Plus className="size-3.5" />
          {t('sessionPlan.addSession')}
        </button>
      )}
      {sessions.length > 1 && (
        <p className="text-[11px] text-muted-foreground">{t('sessionPlan.rotationHint')}</p>
      )}
      <div className="flex flex-wrap items-center gap-1.5">
        <button type="button" className={primaryBtn} disabled={busy || !valid} onClick={submit}>
          {t('sessionPlan.save')}
        </button>
        <button type="button" className={ghostBtn} onClick={onDone}>
          {t('sessionPlan.cancel')}
        </button>
        {canRemove && (
          <button
            type="button"
            className={cn(ghostBtn, 'ml-auto', confirmRemove && 'text-destructive')}
            onClick={() => {
              if (!confirmRemove) return setConfirmRemove(true)
              void remove({ habitId })
                .then(onDone)
                .catch(() => toast.error(t('sessionPlan.error')))
            }}
          >
            <Trash2 className="size-3.5" />
            {confirmRemove ? t('sessionPlan.removeConfirm') : t('sessionPlan.remove')}
          </button>
        )}
      </div>
    </div>
  )
}

function ItemTargetEditor({
  item: it,
  onChange,
}: {
  item: PlanItem
  onChange: (p: Partial<PlanItem>) => void
}) {
  const { t } = useTranslation()
  const n = (v: string) => (v === '' ? undefined : Number(v))
  const field = (
    key: 'sets' | 'reps' | 'weight' | 'seconds' | 'distance' | 'rest',
    label: string,
    step = 1,
  ) => (
    <label className="flex items-center gap-1 text-[11px] text-muted-foreground">
      {label}
      <input
        type="number"
        inputMode="decimal"
        min={0}
        step={step}
        value={it[key] ?? ''}
        placeholder={key === 'rest' ? String(restSeconds(it)) : undefined}
        onChange={(e) => onChange({ [key]: n(e.target.value) })}
        className={cn(input, 'h-7 w-14 px-1.5 text-center font-mono text-xs')}
      />
    </label>
  )
  const setKind = (kind: ItemKind) =>
    onChange(
      kind === 'sets'
        ? { kind, reps: it.reps ?? 10, seconds: undefined, distance: undefined, unit: undefined }
        : kind === 'timed'
          ? { kind, seconds: it.seconds ?? 60, reps: undefined, weight: undefined, unit: undefined }
          : {
              kind,
              sets: 1,
              distance: it.distance ?? 3,
              unit: 'km',
              reps: undefined,
              weight: undefined,
              seconds: undefined,
            },
    )
  return (
    <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
      <select
        value={it.kind}
        aria-label={t('sessionPlan.kind')}
        onChange={(e) => setKind(e.target.value as ItemKind)}
        className={cn(input, 'h-7 cursor-pointer text-xs')}
      >
        <option value="sets">{t('sessionPlan.kindSets')}</option>
        <option value="timed">{t('sessionPlan.kindTimed')}</option>
        <option value="distance">{t('sessionPlan.kindDistance')}</option>
      </select>
      <select
        value={it.move ?? ''}
        aria-label={t('sessionPlan.move')}
        title={t('sessionPlan.move')}
        onChange={(e) => onChange({ move: (e.target.value || undefined) as MoveId | undefined })}
        className={cn(input, 'h-7 max-w-36 cursor-pointer text-xs')}
      >
        <option value="">
          {t('sessionPlan.moveAuto', {
            name: t(`sessionPlan.moves.${inferMove(it.name, it.kind)}`),
          })}
        </option>
        {MOVE_IDS.map((m) => (
          <option key={m} value={m}>
            {t(`sessionPlan.moves.${m}`)}
          </option>
        ))}
      </select>
      {field('sets', t('sessionPlan.sets'))}
      {it.kind === 'sets' && (
        <>
          {field('reps', t('sessionPlan.reps'))}
          {field('weight', t('sessionPlan.weight'), 0.5)}
          <select
            value={it.unit === 'lb' ? 'lb' : 'kg'}
            aria-label={t('sessionPlan.unit')}
            onChange={(e) => onChange({ unit: e.target.value })}
            className={cn(input, 'h-7 cursor-pointer text-xs')}
          >
            <option value="kg">kg</option>
            <option value="lb">lb</option>
          </select>
        </>
      )}
      {it.kind === 'timed' && field('seconds', t('sessionPlan.seconds'), 5)}
      {field('rest', t('sessionPlan.restSeconds'), 15)}
      <select
        value={it.block ?? ''}
        aria-label={t('sessionPlan.part')}
        title={t('sessionPlan.part')}
        onChange={(e) => onChange({ block: (e.target.value || undefined) as PlanItem['block'] })}
        className={cn(input, 'h-7 cursor-pointer text-xs')}
      >
        <option value="">{t('sessionPlan.main')}</option>
        <option value="warmup">{t('sessionPlan.warmup')}</option>
        <option value="cooldown">{t('sessionPlan.cooldown')}</option>
      </select>
      {it.kind === 'timed' && (
        <label className="flex cursor-pointer items-center gap-1 text-[11px] text-muted-foreground">
          <input
            type="checkbox"
            checked={!!it.eachSide}
            onChange={(e) => onChange({ eachSide: e.target.checked || undefined })}
            className="size-3.5 cursor-pointer accent-[var(--primary)]"
          />
          {t('sessionPlan.eachSide')}
        </label>
      )}
      {it.kind === 'distance' && (
        <>
          {field('distance', t('sessionPlan.distance'), 0.1)}
          <select
            value={it.unit ?? 'km'}
            aria-label={t('sessionPlan.unit')}
            onChange={(e) => onChange({ unit: e.target.value })}
            className={cn(input, 'h-7 cursor-pointer text-xs')}
          >
            <option value="km">km</option>
            <option value="mi">mi</option>
            <option value="m">m</option>
          </select>
        </>
      )}
    </div>
  )
}

// A planned habit's row block, laid out like a number habit's (e.g. "0 / 20 min"):
// "Push day · 2 / 6 exercises", a bar, "4 exercises to go", and the same round play
// button, which opens the session fullscreen (or resumes it).
export function HabitSessionRow({ id, className }: { id: Id<'nodes'>; className?: string }) {
  const { t } = useTranslation()
  const habit = useLiveHabit(id)
  const view = useQuery(api.sessionPlans.getPlan, habit?.sessionPlan ? { habitId: id } : 'skip')
  const start = useMutation(api.sessionPlans.startSession)
  const ui = useSessionPlanUi()
  const s = habit?.sessionPlan
  if (!habit || !s) return null
  const session = view?.session
  const finished = session?.status === 'done'
  const total = session && !session.unlogged ? session.items.length : s.items
  const doneItems =
    finished && session?.unlogged
      ? total
      : (session?.items.filter((l) => l.sets.length > 0 && l.sets.every((x) => x.done)).length ?? 0)
  const name = (session?.sessionName ?? s.name) || t('sessionPlan.section')
  const open = () => {
    ui.setFullscreen(id)
    if (!session)
      void start({ habitId: id }).catch(() => {
        ui.setFullscreen(null)
        toast.error(t('sessionPlan.error'))
      })
  }
  return (
    <div className={cn('flex items-center gap-3', className)}>
      <div className="min-w-0 flex-1">
        <p className="flex min-w-0 items-center gap-1 font-mono text-[13px] font-semibold leading-none tabular-nums text-foreground">
          <span className="truncate font-sans font-semibold">{name}</span>
          <span className="shrink-0 font-medium text-muted-foreground">
            {' · '}
            {doneItems} / {t('sessionPlan.exercises', { count: total })}
          </span>
          {finished && (
            <Check
              aria-hidden
              className="size-3.5 shrink-0 text-[var(--habit-accent,var(--primary-text))]"
              strokeWidth={3}
            />
          )}
        </p>
        <div
          className="relative mt-1.5 h-1.5 overflow-hidden rounded-full bg-foreground/[0.08]"
          aria-hidden
        >
          <div
            className="h-full rounded-full bg-[var(--habit-accent,var(--primary))] transition-[width] duration-300"
            style={{ width: `${total ? Math.min(100, (doneItems / total) * 100) : 0}%` }}
          />
        </div>
        <p className="mt-1 font-mono text-[11px] leading-none text-muted-foreground">
          {finished
            ? t('sessionPlan.done')
            : t('fasting.toGo', {
                left: t('sessionPlan.exercises', { count: Math.max(0, total - doneItems) }),
              })}
        </p>
      </div>
      {!finished && (
        <button
          type="button"
          // Not stopped: the row's tap opens the habit's sheet, which hosts the fullscreen view.
          onClick={open}
          aria-label={
            s.name ? t('sessionPlan.startNamed', { name: s.name }) : t('sessionPlan.start')
          }
          title={s.name ? t('sessionPlan.startNamed', { name: s.name }) : t('sessionPlan.start')}
          className="flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-full border border-primary/30 bg-primary/10 text-primary-text transition-colors hover:bg-primary/20 active:scale-95"
        >
          <Play className="size-4" strokeWidth={2.5} />
        </button>
      )}
    </div>
  )
}
